"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Clock, Calendar, User, Share2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function KnowledgeHubContent() {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching
  } = usePosts();
  const articleImageMap: Record<string, string> = {
    "148": "https://images.idgesg.net/images/article/2025/02/3814511-ai-cloud-computing-100976111-large.jpg?auto=webp&quality=85,70",
    "147": "https://images.idgesg.net/images/article/2025/02/3811941-cloud-computing-ai-100975833-large.jpg?auto=webp&quality=85,70",
    "146": "https://images.idgesg.net/images/article/2025/01/3799441-cloud-computing-ai-100974443-large.jpg?auto=webp&quality=85,70"
  };
  if (loading && posts.length === 0) {
    return <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 animate-pulse">
        <div className="lg:col-span-8 space-y-8">
          <div className="w-full aspect-[16/9] bg-muted/10 rounded-lg"></div>
          <div className="space-y-4">
            <div className="h-4 w-32 bg-muted/10 rounded-lg"></div>
            <div className="h-12 w-3/4 bg-muted/10 rounded-lg"></div>
            <div className="h-4 w-full bg-muted/10 rounded-lg"></div>
          </div>
        </div>
        <div className="lg:col-span-4 space-y-12">
          {[1, 2].map(i => <div key={i} data-index={i} className="space-y-4">
              <div className="w-full aspect-video bg-muted/10 rounded-lg"></div>
              <div className="h-6 w-full bg-muted/10 rounded-lg"></div>
            </div>)}
        </div>
      </div>;
  }
  if (error) {
    return <Link to="https://www.infoworld.com/profile/david-linthicum/" newTab className="block">
        <div className="flex flex-col items-center justify-center py-24 text-center space-y-6 bg-destructive/5 rounded-lg border border-destructive/20 backdrop-blur-sm hover:bg-destructive/10 transition-colors duration-300">
          <div className="p-4 rounded-lg bg-destructive/10">
            <SafeHydrate><Calendar className="size-10 text-destructive" /></SafeHydrate>
          </div>
          <div className="space-y-2">
            <h3 className="text-2xl font-default font-bold text-foreground tracking-tight">Insights Offline</h3>
            <p className="text-muted-foreground max-w-md mx-auto">Explore David Linthicum's Cloud Insider blog on InfoWorld for strategic analysis on Generative AI, Cloud Architecture, and enterprise technology ecosystems.</p>
          </div>
        </div>
      </Link>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-32 bg-card/20 backdrop-blur-xl rounded-lg border border-border/50">
        <h3 className="text-3xl font-default font-extrabold text-foreground mb-4 tracking-tighter">No Articles Found</h3>
        <p className="text-muted-foreground font-default">David's latest verified publications are being indexed. Check back soon.</p>
      </div>;
  }
  const featuredPost = posts[0];
  const gridPosts = posts.slice(1);
  return <div className="space-y-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        {}
        <div className="lg:col-span-8 group">
          <Link to={featuredPost.customFields?.article_url || featuredPost.link || "#"} newTab className="block space-y-10">
            <div className="relative overflow-hidden rounded-lg border border-white/5 bg-card/30 aspect-[16/9] shadow-2xl">
              <img src={featuredPost.featuredImage || articleImageMap[featuredPost.id] || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683668/img/featured_article_placeholder.png"} alt={featuredPost.customFields?.article_title || featuredPost.title} className="object-cover w-full h-full transition-transform duration-1000 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent opacity-80" />
              <div className="absolute bottom-8 left-8 right-8">
                <div className="flex items-center gap-4 mb-4">
                  <Badge variant="outline" className="bg-primary/10 border-primary/20 text-primary font-mono uppercase tracking-widest text-[10px] py-1 px-3 rounded-lg">
                    {featuredPost.categories?.[0]?.name || "Featured Insight"}
                  </Badge>
                  <span className="text-[10px] font-mono text-muted-foreground/60 uppercase tracking-widest">
                    InfoWorld Publication
                  </span>
                </div>
                <h3 className="text-3xl md:text-5xl font-default font-extrabold text-foreground leading-[1.1] tracking-tighter group-hover:text-primary transition-colors duration-500">
                  {featuredPost.customFields?.article_title || featuredPost.title}
                </h3>
              </div>
            </div>
            
            <div className="space-y-8 px-2">
              {featuredPost.customFields?.article_excerpt && <p className="text-xl text-muted-foreground/90 line-clamp-3 leading-relaxed font-default max-w-4xl" dangerouslySetInnerHTML={{
              __html: featuredPost.customFields.article_excerpt
            }} />}

              <div className="flex flex-wrap items-center gap-8 text-xs text-muted-foreground font-mono uppercase tracking-widest">
                <div className="flex items-center gap-2.5">
                  <User className="size-4 text-primary" />
                  <span>{featuredPost.customFields?.author_name || "David Linthicum"}</span>
                </div>
                {featuredPost.customFields?.publication_date && <div className="flex items-center gap-2.5">
                    <SafeHydrate><Calendar className="size-4 text-primary" /></SafeHydrate>
                    <time dateTime={featuredPost.customFields.publication_date}>
                      {new Date(featuredPost.customFields.publication_date).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                    </time>
                  </div>}
                <div className="flex items-center gap-2.5">
                  <Clock className="size-4 text-primary" />
                  <span>{featuredPost.customFields?.read_time || "12 min read"}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                {featuredPost.tags?.map((tag, idx) => <Badge key={tag.id} data-index={idx} variant="outline" className="bg-white/5 border-white/10 text-muted-foreground hover:text-primary hover:border-primary/40 transition-all rounded-lg px-4 py-1.5 text-[11px] font-mono">
                    #{tag.name}
                  </Badge>)}
              </div>
            </div>
          </Link>
        </div>

        {}
        <div className="lg:col-span-4 flex flex-col gap-12">
          <div className="flex items-center justify-between pb-4 border-b border-white/5">
            <h4 className="text-sm font-mono font-bold uppercase tracking-[0.3em] text-primary">Latest Updates</h4>
            <Share2 className="size-4 text-muted-foreground/40" />
          </div>
          {gridPosts.map((post, i) => <Link key={post.id} to={post.customFields?.article_url || post.link || "#"} newTab data-index={i} className="group block">
              <Card className="bg-card/30 backdrop-blur-xl border-border/50 hover:border-primary/30 transition-all duration-500 p-0 overflow-hidden shadow-lg">
                <div className="relative aspect-video overflow-hidden bg-muted/5">
                  <img src={post.featuredImage || articleImageMap[post.id] || `/img/article_thumb_${i % 3 + 1}.png`} alt={post.customFields?.article_title || post.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-primary">
                      {post.categories?.[0]?.name || "Analysis"}
                    </span>
                    <div className="h-px w-4 bg-white/10" />
                    <span className="text-[10px] font-mono text-muted-foreground/50 uppercase tracking-widest">
                      {post.customFields?.read_time || "8 min"}
                    </span>
                  </div>
                  <h4 className="text-lg font-default font-bold text-foreground leading-tight group-hover:text-primary transition-colors duration-300 line-clamp-2 tracking-tight">
                    {post.customFields?.article_title || post.title}
                  </h4>
                  <div className="pt-2">
                    <span className="text-[10px] font-mono text-muted-foreground/60 uppercase tracking-widest flex items-center gap-2">
                      <SafeHydrate><Calendar className="size-3" /></SafeHydrate>
                      {post.customFields?.publication_date ? new Date(post.customFields.publication_date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  }) : "Recent"}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>)}
        </div>
      </div>

      {hasNext && <div className="flex justify-center pt-16 border-t border-white/5">
          <Button variant="outline" size="lg" onClick={async () => await loadMore()} disabled={isRefetching} className="min-w-[280px] border-primary/30 hover:border-primary hover:bg-primary/10 text-primary rounded-lg font-mono uppercase tracking-widest text-xs transition-all duration-500 shadow-xl group">
            {isRefetching ? "Synchronizing Data..." : "Access Full Archive"}
            {!isRefetching && <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />}
          </Button>
        </div>}
    </div>;
}
export default function LatestInsights() {
  const wp_query_params = {
    post_type: "infoworld_article",
    per_page: 3,
    status: "publish",
    meta_query: [{
      key: "author_name",
      value: "David Linthicum",
      compare: "="
    }]
  };
  return <section data-section-id="243966" id="latest-insights" className="bg-background text-foreground py-24 md:py-40 px-4 md:px-8 lg:px-12 overflow-hidden relative">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-primary/40 to-transparent" />
        <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-primary/40 to-transparent" />
        <div className="absolute top-1/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="mb-20 md:mb-32">
          <div className="flex items-center gap-6 mb-8">
            <div className="h-[2px] w-16 bg-primary" />
            <span className="text-xs font-mono font-bold uppercase tracking-[0.4em] text-primary">
              Architectural Intelligence
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end">
            <h2 className="text-5xl md:text-8xl font-default font-extrabold tracking-tighter leading-[0.9]">
              Latest <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary/80 to-secondary">Insights</span>
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed font-default max-w-xl lg:pb-2">
              David Linthicum's Cloud Insider blog on InfoWorld. Featuring strategic insights on Generative AI, Cloud Architecture, and enterprise technology. Authored by internationally recognized cloud computing expert and author of 13 books.
            </p>
          </div>
        </div>

        <PostsProvider wp_query={wp_query_params} className="knowledge-hub-posts">
          <KnowledgeHubContent />
        </PostsProvider>
      </div>
    </section>;
}